import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Download, CreditCard, RefreshCw, FileText, Wallet, Tag, AlertTriangle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import MainLayoutApple from '../components/apple-ui/MainLayoutApple';
import { useTheme } from '@/contexts/ThemeContext';
import { usePlan } from '@/contexts/PlanContext';

const API = process.env.REACT_APP_BACKEND_URL;

const PLAN_BADGE = {
  free: { label: 'Free', color: 'bg-gray-100 text-gray-700' },
  payg: { label: 'Pay-as-you-go', color: 'bg-amber-100 text-amber-700' },
  starter: { label: 'Starter', color: 'bg-blue-100 text-blue-700' },
  professional: { label: 'Professional', color: 'bg-purple-100 text-purple-700' },
  business: { label: 'Business', color: 'bg-green-100 text-green-700' },
};

export default function BillingPage(props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isDarkMode } = useTheme();
  const { refreshPlanInfo } = usePlan();
  const [planInfo, setPlanInfo] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoCode, setPromoCode] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);
  const promoInputRef = useRef(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
    const codeFromUrl = searchParams.get('code');
    if (codeFromUrl) {
      setPromoCode(codeFromUrl.toUpperCase());
      setTimeout(() => promoInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 600);
    }
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [planRes, invoiceRes] = await Promise.all([
        axios.get(`${API}/api/pricing/plan-info`, { headers }),
        axios.get(`${API}/api/user/invoices`, { headers }),
      ]);
      setPlanInfo(planRes.data);
      setInvoices((invoiceRes.data.invoices || []).slice(0, 5));
    } catch {
      toast.error('Failed to load billing info');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (invoiceNumber) => {
    try {
      const res = await axios.get(
        `${API}/api/user/invoices/${invoiceNumber}/download`,
        { headers, responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${invoiceNumber}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download invoice');
    }
  };

  const handlePromoRedeem = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/promo/redeem`,
        { code: promoCode.trim() },
        { headers }
      );
      toast.success(res.data.message || 'Promo code applied!');
      setPromoCode('');
      // Refresh global PlanContext so feature gates unlock immediately
      await refreshPlanInfo();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired promo code');
    } finally {
      setPromoLoading(false);
    }
  };

  const plan = planInfo?.plan || 'free';
  const isPayg = plan === 'payg';
  const planBadge = PLAN_BADGE[plan] || PLAN_BADGE.free;
  const wordsUsed = planInfo?.monthly_usage || 0;
  const walletWords = planInfo?.wallet_words || 0;
  const walletBalance = planInfo?.wallet_balance || 0;
  const wordsQuota = isPayg ? (walletWords + wordsUsed) : (planInfo?.monthly_quota || 1000);
  const usagePercent = wordsQuota > 0 ? Math.min(100, Math.round((wordsUsed / wordsQuota) * 100)) : 0;
  const promoExpired = planInfo?.promo_expired || false;
  const promoExpiringSoon = planInfo?.promo_expiring_soon || false;
  const daysRemaining = planInfo?.days_remaining;

  const formatResetDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Kolkata',
    });
  };

  const cardBg = isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-400' : 'text-gray-500';

  return (
    <MainLayoutApple {...props}>
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className={`text-2xl font-bold ${textPrimary}`}>Billing</h1>
          <p className={`text-sm mt-1 ${textSecondary}`}>Your plan, usage, and invoices</p>
        </div>

        {/* Promo expired banner */}
        {!loading && promoExpired && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200 dark:bg-red-900/20 dark:border-red-800">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-red-700 dark:text-red-400 text-sm">Free trial has ended</div>
              <div className="text-xs text-red-600 dark:text-red-400 mt-0.5">Subscribe to keep access to Professional features.</div>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              className="shrink-0 bg-red-600 hover:bg-red-700 text-white text-xs px-3 py-1.5 h-auto"
            >
              Subscribe now
            </Button>
          </div>
        )}

        {/* Promo expiring soon banner */}
        {!loading && !promoExpired && promoExpiringSoon && (
          <div className="flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 dark:bg-amber-900/20 dark:border-amber-800">
            <Clock className="w-5 h-5 text-amber-500 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-amber-700 dark:text-amber-400 text-sm">
                Free trial expires in {daysRemaining} day{daysRemaining !== 1 ? 's' : ''}
              </div>
              <div className="text-xs text-amber-600 dark:text-amber-400 mt-0.5">Subscribe before it ends to keep uninterrupted access.</div>
            </div>
            <Button
              onClick={() => navigate('/pricing')}
              className="shrink-0 bg-amber-600 hover:bg-amber-700 text-white text-xs px-3 py-1.5 h-auto"
            >
              Subscribe
            </Button>
          </div>
        )}

        {/* Plan Card */}
        <Card className={`p-6 border ${cardBg}`}>
          {loading ? (
            <div className={`text-sm ${textSecondary}`}>Loading...</div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CreditCard className={`w-5 h-5 ${textSecondary}`} />
                  <div>
                    <div className={`font-semibold ${textPrimary}`}>{planInfo?.plan_name || 'Free'} Plan</div>
                    <div className={`text-xs ${textSecondary}`}>Current plan</div>
                  </div>
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${planBadge.color}`}>
                  {planBadge.label}
                </span>
              </div>

              {/* Usage / Wallet */}
              {isPayg ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className={`flex items-center gap-1.5 ${textSecondary}`}><Wallet className="w-3.5 h-3.5" />Wallet balance</span>
                    <span className={`font-semibold ${textPrimary}`}>₹{walletBalance.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={textSecondary}>Wallet words remaining</span>
                    <span className={`font-medium ${textPrimary}`}>{walletWords.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className={textSecondary}>Words used this month</span>
                    <span className={`font-medium ${textPrimary}`}>{wordsUsed.toLocaleString()}</span>
                  </div>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <div className="flex justify-between text-sm">
                    <span className={textSecondary}>Words used this month</span>
                    <span className={`font-medium ${textPrimary}`}>
                      {wordsUsed.toLocaleString()} / {wordsQuota === 999999999 ? '∞' : wordsQuota.toLocaleString()}
                    </span>
                  </div>
                  <div className={`h-2 rounded-full ${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                    <div
                      className={`h-2 rounded-full transition-all ${usagePercent >= 90 ? 'bg-red-500' : usagePercent >= 70 ? 'bg-amber-500' : 'bg-blue-500'}`}
                      style={{ width: `${usagePercent}%` }}
                    />
                  </div>
                  <div className={`text-xs ${textSecondary}`}>{usagePercent}% used</div>
                </div>
              )}

              {/* Renewal Date — subscription plans only */}
              {!isPayg && planInfo?.usage_reset_date && (
                <div className="flex items-center justify-between text-sm">
                  <span className={textSecondary}>Next renewal</span>
                  <span className={`font-medium ${textPrimary}`}>{formatResetDate(planInfo.usage_reset_date)}</span>
                </div>
              )}

              {/* Upgrade Button */}
              {plan !== 'business' && (
                <Button
                  onClick={() => navigate('/pricing')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white"
                >
                  Upgrade Plan
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Promo Code */}
        <Card className={`p-6 border ${cardBg}`}>
          <div className="flex items-center gap-2 mb-4">
            <Tag className={`w-4 h-4 ${textSecondary}`} />
            <h2 className={`font-semibold ${textPrimary}`}>Promo Code</h2>
          </div>
          <div className="flex gap-2">
            <input
              ref={promoInputRef}
              type="text"
              value={promoCode}
              onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
              onKeyDown={(e) => e.key === 'Enter' && handlePromoRedeem()}
              placeholder="Enter promo code"
              className={`flex-1 px-3 py-2 text-sm rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode
                  ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                  : 'bg-white border-gray-200 text-gray-900 placeholder-gray-400'
              }`}
            />
            <Button
              onClick={handlePromoRedeem}
              disabled={promoLoading || !promoCode.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4"
            >
              {promoLoading ? 'Applying…' : 'Apply'}
            </Button>
          </div>
        </Card>

        {/* Invoices */}
        <Card className={`p-6 border ${cardBg}`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`font-semibold ${textPrimary}`}>Recent Invoices</h2>
            <button onClick={fetchData} className={`text-xs ${textSecondary} hover:opacity-70 flex items-center gap-1`}>
              <RefreshCw className="w-3 h-3" /> Refresh
            </button>
          </div>

          {loading ? (
            <div className={`text-sm ${textSecondary}`}>Loading...</div>
          ) : invoices.length === 0 ? (
            <div className={`text-sm ${textSecondary} text-center py-6`}>No invoices yet</div>
          ) : (
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {invoices.map((inv) => (
                <div key={inv.invoice_number} className="flex items-center justify-between py-3">
                  <div className="flex items-center gap-3">
                    <FileText className={`w-4 h-4 ${textSecondary}`} />
                    <div>
                      <div className={`text-sm font-medium ${textPrimary}`}>{inv.invoice_number}</div>
                      <div className={`text-xs ${textSecondary}`}>
                        {inv.created_at ? new Date(inv.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }) : '—'}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {inv.total_amount != null && (
                      <span className={`text-sm font-medium ${textPrimary}`}>
                        ₹{Number(inv.total_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                      </span>
                    )}
                    <button
                      onClick={() => handleDownload(inv.invoice_number)}
                      className={`p-1.5 rounded-lg text-blue-600 transition-colors ${isDarkMode ? 'hover:bg-blue-900/20' : 'hover:bg-blue-50'}`}
                      title="Download PDF"
                    >
                      <Download className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Footer note */}
        <p className={`text-xs text-center ${textSecondary}`}>
          Payments are securely managed by Cashfree Payments
        </p>
      </div>
    </MainLayoutApple>
  );
}
